// Post-auth redirect stash (Aug 2026).
//
// When a LOGGED-OUT visitor clicks "Start Free Trial" on the marketing pricing
// cards, the checkout page (/checkout?plan=X) can't create a Stripe session
// without an authenticated user — so it stashes the intended checkout URL here
// and sends them to sign up. Once they finish authenticating AND verifying their
// email, the auth pages consume this stash and drop them straight back on
// checkout to start their trial (instead of the default /gallery landing).
//
// The stash survives the whole hop chain in the same tab (sign-up →
// verify-email → post-verify) because sessionStorage persists across in-tab
// navigations + full reloads. It's consumed exactly once.

const KEY = 'sp:post_auth_checkout';

/** Remember where to send the user after they authenticate. Call this before
 *  routing a logged-out user to sign-up (e.g. from the checkout guard). */
export function stashPostAuthRedirect(path: string): void {
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    // private mode / storage blocked — funnel just falls back to the default
    // landing; nothing breaks.
  }
}

/** Return the stashed post-auth destination and CLEAR it, or null if none.
 *  Only same-origin app paths (leading '/', not '//') are honored so this can
 *  never be turned into an open-redirect. Safe to call at every post-auth nav
 *  point — returns null when there's nothing stashed. */
export function consumePostAuthRedirect(): string | null {
  let v: string | null = null;
  try {
    v = sessionStorage.getItem(KEY);
    if (v !== null) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (v && v.startsWith('/') && !v.startsWith('//')) return v;
  return null;
}
